import { Editor } from '@monaco-editor/react';
import { cx } from 'class-variance-authority';
import type * as monacoTypes from 'monaco-editor';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getMonacoConfig, Shimmer } from '../..';
import useIsDarkMode from '../../hooks/use-is-dark-mode';
import { useMonaco } from '../monaco/monaco-provider';
import { getJsonThemeName } from '../template-editor/utils/monaco-config';
import type { TemplateInputProps } from '../template-input/template-input';
import { createTemplateCompletionProvider, setupAutoTrigger } from './template-autocomplete';

const MIN_HEIGHTS = { medium: 160, large: 328 } as const;
const MAX_HEIGHT = 656;

export type JsonEditorProps = {
  value?: string;
  onChange?: (val: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  /**
   * The height the editor starts at. It grows with its content up to 656px, then scrolls.
   */
  size?: keyof typeof MIN_HEIGHTS;
  /**
   * Fixes the height instead, for an editor that fills a container it measures.
   */
  height?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * If provided, enables template variable autocomplete with {{ }} syntax.
   * Template variables will be suggested when typing {{ in the editor.
   */
  autocompleteItems?: TemplateInputProps['autocompleteItems'];
  /**
   * Callback fired when Monaco's validation changes.
   * Returns validation markers (errors/warnings) from Monaco.
   */
  onValidate?: (markers: monacoTypes.editor.IMarker[]) => void;

  /**
   * JSON schema to validate the editor content.
   */
  schemas?: monacoTypes.languages.json.DiagnosticsOptions['schemas'];
  allowTrailingCommas?: boolean;
  /**
   * Disables JSON syntax validation and squiggly error lines.
   * Useful when we don't want to enforce proper JSON.
   */
  disableJsonSchemaValidation?: boolean;
};

export const JsonEditor = forwardRef<HTMLDivElement, JsonEditorProps>(
  (
    {
      value = '',
      onChange,
      isDisabled = false,
      autocompleteItems,
      placeholder,
      onValidate,
      schemas,
      size = 'large',
      height,
      onFocus,
      onBlur,
      allowTrailingCommas,
      disableJsonSchemaValidation = false,
    },
    ref,
  ) => {
    const [contentHeight, setContentHeight] = useState(0);
    const editorHeight = height ?? Math.min(Math.max(contentHeight, MIN_HEIGHTS[size]), MAX_HEIGHT);
    const [isEditorMounted, setIsEditorMounted] = useState(false);
    const [editorLanguage, setEditorLanguage] = useState('json');
    const { monaco } = useMonaco();
    const isDarkMode = useIsDarkMode();
    const themeName = getJsonThemeName(isDarkMode);
    const disposables = useRef<monacoTypes.IDisposable[]>([]);
    const editorRef = useRef<monacoTypes.editor.IStandaloneCodeEditor | null>(null);
    const decorationsRef = useRef<string[]>([]);

    // Expose focus and blur methods for react-hook-form compatibility
    useImperativeHandle(ref, () => {
      return {
        focus: () => {
          editorRef.current?.focus();
        },
        blur: () => {
          editorRef.current?.getDomNode()?.blur();
        },
      } as HTMLDivElement;
    }, []);

    const onMount = (editor: monacoTypes.editor.IStandaloneCodeEditor) => {
      editorRef.current = editor;

      editor.updateOptions({ theme: themeName });

      // Configure JSON language features
      if (monaco?.languages?.json) {
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: !disableJsonSchemaValidation,
          schemaValidation: disableJsonSchemaValidation ? 'ignore' : 'error',
          schemaRequest: 'warning',
          allowComments: true,
          trailingCommas: allowTrailingCommas ? 'ignore' : 'error',
          schemas,
        });

        monaco.languages.json.jsonDefaults.setModeConfiguration({
          documentFormattingEdits: true,
          documentRangeFormattingEdits: true,
          completionItems: Boolean(schemas),
          hovers: Boolean(schemas),
          documentSymbols: false,
          tokens: true,
          colors: false,
          foldingRanges: false,
          diagnostics: !disableJsonSchemaValidation,
          selectionRanges: false,
        });
      }

      // Listen to validation markers if callback provided
      if (onValidate && monaco) {
        const model = editor.getModel();
        if (model) {
          // Function to update decorations based on markers
          const updateDecorations = (markers: monacoTypes.editor.IMarker[]) => {
            const newDecorations: monacoTypes.editor.IModelDeltaDecoration[] = markers.map(marker => ({
              range: new monaco.Range(
                marker.startLineNumber,
                marker.startColumn,
                marker.endLineNumber,
                marker.endColumn,
              ),
              options: {
                className: 'json-error-decoration',
                inlineClassName: 'json-error-inline',
              },
            }));

            decorationsRef.current = editor.deltaDecorations(decorationsRef.current, newDecorations);
            onValidate(markers);
          };

          // Initial validation
          const markers = monaco.editor.getModelMarkers({ resource: model.uri });
          updateDecorations(markers);

          // Listen for marker changes
          const markerListener = monaco.editor.onDidChangeMarkers(() => {
            const updatedMarkers = monaco.editor.getModelMarkers({ resource: model.uri });
            updateDecorations(updatedMarkers);
          });
          disposables.current.push(markerListener);
        }
      }

      // Track language changes; the registration effect below re-registers the
      // provider on the new language.
      const langChange = editor.onDidChangeModelLanguage(e => setEditorLanguage(e.newLanguage));
      disposables.current.push(langChange);

      setContentHeight(editor.getContentHeight());
      disposables.current.push(editor.onDidContentSizeChange(e => setContentHeight(e.contentHeight)));

      setIsEditorMounted(true);
    };

    // Register/re-register the template completion provider whenever
    // `autocompleteItems` or the model language changes. This ensures we don't
    // hold a stale closure when items load asynchronously after the editor mounts.
    useEffect(() => {
      if (!isEditorMounted || !autocompleteItems || !monaco || !editorRef.current) return;

      const provider = createTemplateCompletionProvider(monaco, autocompleteItems);
      const providerDisp = monaco.languages.registerCompletionItemProvider(editorLanguage, provider);
      const autoTriggerDisp = setupAutoTrigger(editorRef.current);

      return () => {
        providerDisp.dispose();
        autoTriggerDisp.dispose();
      };
    }, [autocompleteItems, monaco, editorLanguage, isEditorMounted]);

    useEffect(() => {
      if (editorRef.current) {
        editorRef.current.updateOptions({ theme: themeName });
      }
    }, [themeName]);

    // Proper cleanup
    useEffect(() => {
      return () => {
        disposables.current.forEach(d => {
          d.dispose();
        });
        disposables.current = [];
      };
    }, []);

    if (!monaco) {
      return <Shimmer className="w-full" height={editorHeight} />;
    }

    return (
      // aria-busy holds until Monaco mounts so tests and assistive tech wait for the editor to lay
      // out its final height instead of reading the minimum-height loading frame.
      <div className="overflow-visible w-full relative" aria-busy={!isEditorMounted}>
        <Editor
          wrapperProps={{ onFocus, onBlur }}
          className={cx(
            `
          relative w-full rounded-md
          bg-secondary outline-none
          px-3
          [&_.monaco-editor]:absolute!
          [&_.monaco-editor-background]:bg-secondary!
          [&_.margin-view-overlays]:bg-secondary!
          [&_.sticky-line-content]:bg-secondary!
          [&_.sticky-line-number]:bg-secondary!
          [&_.json-error-inline]:underline!
          [&_.json-error-inline]:decoration-wavy!
          [&_.json-error-inline]:decoration-1!
          [&_.json-error-inline]:[text-decoration-color:#ef4444]!
          [&_.squiggly-error]:hidden!
          [&_.squiggly-d-error]:hidden!
          [&_.zone-widget]:hidden!
          [&_.findMatch]:bg-transparent!
          [&_.ime-input]:bg-transparent!
        `,
            {
              'bg-secondary! border-tertiary! opacity-60': isDisabled,
            },
          )}
          height={`${editorHeight}px`}
          language="json"
          value={value}
          onChange={v => onChange?.(v ?? '')}
          options={getMonacoConfig({
            formatOnPaste: true,
            formatOnType: true,
            lineNumbers: 'on',
            cursorStyle: 'line',
            cursorWidth: 2,
            cursorBlinking: 'blink',
            quickSuggestions: false,
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            hover: { enabled: Boolean(schemas) },
            suggest: {
              showWords: false,
              showSnippets: false,
              showVariables: true,
              showKeywords: false,
              showFunctions: false,
              showClasses: false,
              showMethods: false,
              showProperties: Boolean(schemas),
              localityBonus: true,
              filterGraceful: false,
              snippetsPreventQuickSuggestions: false,
              showInlineDetails: true,
              preview: true,
            },
            padding: { top: 12, bottom: 12 },
            lineDecorationsWidth: 12,
            lineHeight: 20,
            lineNumbersMinChars: 0,
            placeholder: placeholder,
            size: 'default',
            disabled: isDisabled,
            fontSize: 13,
            fontFamily: '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
            scrollbar: {
              alwaysConsumeMouseWheel: false,
            },
          })}
          onMount={onMount}
          loading={<Shimmer className="w-full h-full" />}
        />
      </div>
    );
  },
);

JsonEditor.displayName = 'JsonEditor';
